import { useCallback, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

export const positivePage = {
  defaultValue: 1,
  parse: (value) => {
    const page = Number(value);
    return Number.isInteger(page) && page > 0 ? page : 1;
  },
  serialize: String,
};

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const pageSizeParam = {
  defaultValue: 10,
  parse: (value) => {
    const pageSize = Number(value);
    return PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : 10;
  },
  serialize: String,
};

export const stringParam = (defaultValue = "") => ({ defaultValue });

export const enumParam = (values, defaultValue) => ({
  defaultValue,
  parse: (value) => (values.includes(value) ? value : defaultValue),
});

export const parseListQuery = (search, schema) => {
  const params = new URLSearchParams(search || "");

  return Object.keys(schema).reduce((result, key) => {
    const definition = schema[key];
    const rawValue = params.get(key);
    const value = rawValue === null ? definition.defaultValue : rawValue;
    result[key] = definition.parse ? definition.parse(value) : value;
    return result;
  }, {});
};

export const serializeListQuery = (state, schema) => {
  const params = new URLSearchParams();

  Object.keys(schema).forEach((key) => {
    const definition = schema[key];
    const value = state[key];
    if (value === undefined || value === null || value === definition.defaultValue) return;

    const serialized = definition.serialize
      ? definition.serialize(value)
      : String(value);
    if (serialized !== "") params.set(key, serialized);
  });

  const query = params.toString();
  return query ? `?${query}` : "";
};

export const hasNonDefaultQuery = (state, schema, keys = Object.keys(schema)) =>
  keys.some((key) => {
    const definition = schema[key];
    return definition && state[key] !== definition.defaultValue;
  });

export const useListQueryState = (schema) => {
  const routerHistory = useHistory();
  const location = useLocation();
  const state = useMemo(
    () => parseListQuery(location.search, schema),
    [location.search, schema]
  );
  const canonicalSearch = useMemo(
    () => serializeListQuery(state, schema),
    [schema, state]
  );

  useEffect(() => {
    if (location.search !== canonicalSearch) {
      routerHistory.replace({
        pathname: location.pathname,
        search: canonicalSearch,
        hash: location.hash,
        state: location.state,
      });
    }
  }, [canonicalSearch, location, routerHistory]);

  const updateQuery = useCallback(
    (updates) => {
      const nextState = {
        ...state,
        ...(typeof updates === "function" ? updates(state) : updates),
      };
      const nextSearch = serializeListQuery(nextState, schema);
      if (nextSearch === location.search) return;

      routerHistory.replace({
        pathname: location.pathname,
        search: nextSearch,
        hash: location.hash,
        state: location.state,
      });
    },
    [location, routerHistory, schema, state]
  );

  const resetQuery = useCallback(
    (keys = Object.keys(schema)) => {
      const defaults = keys.reduce((result, key) => {
        if (schema[key]) result[key] = schema[key].defaultValue;
        return result;
      }, {});

      updateQuery(defaults);
    },
    [schema, updateQuery]
  );

  return { state, updateQuery, resetQuery, location };
};

export const createDetailLocation = (pathname, location) => ({
  pathname,
  search: location.search,
  state: {
    returnTo: `${location.pathname}${location.search}`,
    fromList: true,
  },
});

export const isSafeListReturnPath = (value, listPath) =>
  typeof value === "string" &&
  (value === listPath || value.startsWith(`${listPath}?`));

export const getNestedReturnPath = (location, key, listPath) => {
  const params = new URLSearchParams(location?.search || "");
  const queryReturnTo = params.get(key);
  const stateReturnTo = location?.state?.[key] || location?.state?.returnTo;

  if (isSafeListReturnPath(queryReturnTo, listPath)) return queryReturnTo;
  if (isSafeListReturnPath(stateReturnTo, listPath)) return stateReturnTo;
  return "";
};

export const createReturnContextSearch = (key, returnTo, listPath) => {
  if (!isSafeListReturnPath(returnTo, listPath) || returnTo === listPath) return "";
  const params = new URLSearchParams();
  params.set(key, returnTo);
  return `?${params.toString()}`;
};

export const returnToList = (routerHistory, location, fallbackPath) => {
  const returnTo = location.state?.returnTo;
  const hasSafeReturn =
    location.state?.fromList &&
    typeof returnTo === "string" &&
    returnTo.startsWith("/") &&
    !returnTo.startsWith("//");

  if (hasSafeReturn) {
    routerHistory.goBack();
    return;
  }

  routerHistory.replace({
    pathname: fallbackPath,
    search: location.search,
  });
};
